/**
 * "คำสอน AI" card (api/app/ai/learn.py): a "/ln …" typed on a TEST account becomes a proposed rule in
 * คลังความรู้ › กติกาการตอบ. Pure helpers (tested in test/learn.spec.ts): the status line and who may do what — the
 * same role rules as the knowledge page (owner approves; head edits a proposal; staff reads).
 */
import type { LearnItem, LearnOverlap, LearnRuleStatus } from '../api/types'
import type { StaffRole } from '../api/session'

export type LearnTone = 'wait' | 'ok' | 'off' | 'none'

export interface LearnStatus {
  label: string
  tone: LearnTone
}

export function learnStatus(item: LearnItem): LearnStatus {
  const r = item.rule
  if (!r) return { label: 'ไม่ได้สร้างกติกา', tone: 'none' }
  switch (r.status) {
    case 'approved':
      return { label: r.pending_text ? 'AI ใช้อยู่ · มีข้อเสนอแก้' : 'AI ใช้แล้ว', tone: 'ok' }
    case 'rejected':
      return { label: 'ไม่ใช้', tone: 'off' }
    case 'retired':
      return { label: 'ปิดใช้', tone: 'off' }
  }
  return { label: 'รอเจ้าของอนุมัติ', tone: 'wait' }
}

/** Who the AI/staff message it answers came from, for "ตอบต่อจาก …". */
export function answeringWho(item: LearnItem): string {
  const a = item.answering
  if (!a) return ''
  if (a.author === 'ai') return 'AI'
  if (a.author === 'staff') return a.name ?? 'พนักงาน'
  return 'เพจ'
}

export interface LearnPerms {
  approve: boolean
  reject: boolean
  edit: boolean
  /** owner, waiting, and a rule it contradicts is still in use/waiting: approve becomes a choice of three
   *  (ใช้ข้อใหม่แทนข้อเดิม · ใช้ทั้งคู่ · ไม่ใช้ข้อใหม่) — owner 27 ก.ย. 69 "ถ้า สอน AI กับ กติกาขัดแย้งกันละ" */
  choose: boolean
}

export function learnPerms(role: StaffRole | null | undefined, item: LearnItem): LearnPerms {
  const r = item.rule
  const owner = role === 'owner'
  const editor = owner || role === 'head'
  if (!r) return { approve: false, reject: false, edit: false, choose: false }
  const waiting = r.status === 'pending_approval'
  return {
    approve: owner && waiting,
    reject: owner && waiting,
    choose: owner && waiting && liveConflicts(item.overlaps).length > 0,
    // a rejected proposal is not edited (teach again instead); live rules: owner edits at once, head proposes
    edit: editor && r.status !== 'rejected',
  }
}

/** The small line under the rule about the AI rewrite. */
export function rewriteNote(item: LearnItem): string | null {
  if (!item.rule || item.rule.status !== 'pending_approval') return null
  if (item.rewrite === 'pending') return 'AI กำลังเรียบเรียงเป็นกติกา…'
  if (item.rewrite === 'failed') return 'AI เรียบเรียงไม่สำเร็จ ใช้ข้อความที่สอนเป็นกติกา'
  if (item.rewrite === 'done' && !item.rule.edited && item.ai_text && item.ai_text === item.rule.text)
    return 'AI เรียบเรียงจากคำสอนแล้ว ตรวจก่อนอนุมัติ'
  return null
}

export const LEARN_RULE_MAX = 200

/** Rules it contradicts that are still in use or waiting (the server keeps each one's status live). */
export function liveConflicts(overlaps: readonly LearnOverlap[] | null | undefined): LearnOverlap[] {
  return (overlaps ?? []).filter(
    (o) => o.relation === 'conflict' && (o.status === 'approved' || o.status === 'pending_approval'),
  )
}

/** The status of a rule it repeats / contradicts, next to that rule. */
export const OVERLAP_STATUS: Record<LearnRuleStatus, string> = {
  approved: 'ใช้อยู่',
  pending_approval: 'รออนุมัติ',
  retired: 'ปิดใช้แล้ว',
  rejected: 'ไม่ใช้',
}

/** The heading over the rules it repeats or contradicts (null = none). `owner` = the one who decides. */
export function overlapTitle(item: LearnItem, owner: boolean): string | null {
  const conflicts = item.overlaps.filter((o) => o.relation === 'conflict')
  if (!conflicts.length) return item.overlaps.length ? 'ซ้ำกับกติกาที่มีอยู่' : null
  const live = liveConflicts(item.overlaps)
  switch (item.rule?.status) {
    case 'pending_approval':
      if (!live.length) return 'เคยขัดกับกติกาเหล่านี้ ตอนนี้ไม่ได้ใช้แล้ว'
      return owner ? 'ขัดกับกติกาที่มีอยู่ เลือกว่าจะใช้ข้อไหน' : 'อาจขัดกับกติกาที่มีอยู่ เจ้าของเป็นคนเลือกว่าจะใช้ข้อไหน'
    case 'approved':
      return live.some((o) => o.status === 'approved')
        ? 'ใช้คู่กับกติกาเดิม ถ้าขัดกัน AI ยึดข้อที่อนุมัติทีหลัง'
        : 'ใช้แทนกติกาเดิมแล้ว'
  }
  return 'อาจขัดกับกติกาที่มีอยู่'
}

/** "ใช้ข้อใหม่แทนข้อเดิม" / "ใช้ทั้งคู่" for n rules it contradicts. */
export function choiceLabels(n: number): { replace: string; both: string } {
  return n > 1
    ? { replace: `ใช้ข้อใหม่แทน ${n} ข้อเดิม`, both: 'ใช้ทุกข้อ' }
    : { replace: 'ใช้ข้อใหม่แทนข้อเดิม', both: 'ใช้ทั้งคู่' }
}
